import * as React from "react"
import { LoadingBlock, LoadingCircle } from "@digits-shared/components/Loaders"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgPlus } from "@digits-shared/components/SVGIcons/line/Plus.svg"
import borders from "@digits-shared/themes/borders"
import colors from "@digits-shared/themes/colors"
import fonts, { BodyText, LabelText } from "@digits-shared/themes/typography"
import styled from "styled-components"

export const DETAILS_CONTENT_ID = "settings-details-content"

export const DETAILS_MODAL_NAVIGATION_AREA_WIDTH = 300
export const DETAILS_MODAL_CONTENT_AREA_WIDTH = 772
export const DETAILS_MODAL_CONTENT_AREA_HEIGHT = 728
const DETAILS_MODAL_NAV_OVERLAP = 24
export const DETAILS_MODAL_MODAL_FULL_WIDTH =
  DETAILS_MODAL_CONTENT_AREA_WIDTH + DETAILS_MODAL_NAVIGATION_AREA_WIDTH - DETAILS_MODAL_NAV_OVERLAP
export const DETAILS_MODAL_PRIMARY_PADDING = 32
export const DETAILS_MODAL_HEADER_HEIGHT = 40
export const DETAILS_MODAL_HEADER_MARGIN = 12
export const DETAILS_MODAL_CONTENT_AREA_MAIN_HEIGHT =
  DETAILS_MODAL_CONTENT_AREA_HEIGHT -
  DETAILS_MODAL_HEADER_HEIGHT -
  DETAILS_MODAL_HEADER_MARGIN -
  2 * DETAILS_MODAL_PRIMARY_PADDING

export const MainContentWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  border-radius: ${borders.radius.container}px;
  height: ${DETAILS_MODAL_CONTENT_AREA_HEIGHT}px;
  max-height: ${DETAILS_MODAL_CONTENT_AREA_HEIGHT}px;
  width: ${DETAILS_MODAL_CONTENT_AREA_WIDTH}px;
  max-width: ${DETAILS_MODAL_CONTENT_AREA_WIDTH}px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border: 1.5px solid ${colors.translucentWhite40};
  background: #e4ebf2;
  backdrop-filter: blur(10px);
`

export const HeaderContainer = styled.div`
  display: flex;
  height: ${DETAILS_MODAL_HEADER_HEIGHT}px;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  z-index: 1;
  margin-bottom: ${DETAILS_MODAL_HEADER_MARGIN}px;
`

export const ModalHeading = styled.h1`
  margin: 0;
  ${fonts.scale.h1};
  font-weight: ${fonts.weight.heavy};
`

export const ScrollContainer = styled.div<{
  $isEditing?: boolean
}>`
  padding-top: 10px;
  height: ${({ $isEditing }) => ($isEditing ? "calc(100% - 20px - 72px)" : "calc(100% - 20px)")};
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 32px;

  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const Container = styled.div`
  background: ${colors.translucentWhite60};
  border-radius: 16px;
  width: 100%;
  padding: 16px;
`

export const DescriptionArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const DetailsModalTitle = styled(BodyText)`
  color: ${colors.secondary};
  font-weight: ${fonts.weight.heavy};
`

export const DetailsModalDescription = styled.div`
  color: ${colors.secondary90};

  font-size: 14px;
`

export const FullWidthContainerWrapper = styled.div`
  position: absolute;
  top: 24px;
  left: 24px;
  width: calc(${DETAILS_MODAL_MODAL_FULL_WIDTH}px - 48px);
  height: calc(${DETAILS_MODAL_CONTENT_AREA_HEIGHT}px - 48px);
  border-radius: ${borders.radius.container}px;
  border: 1px solid ${colors.white};
  background: #e2e9f3;
  box-shadow: 0 0 8px 0 ${colors.translucentSecondary20};
  z-index: 1;
  display: flex;
  flex-direction: column;
`

export const FooterWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  height: 72px;
  min-height: 72px;
  background: ${colors.white};
  padding: 0 32px;
  border-radius: 0 0 ${borders.radius.container}px ${borders.radius.container}px;
`

export const FullWidthContainerFooter = styled.div`
  width: 100%;
  background: ${colors.translucentWhite50};
  height: 88px;
  border-radius: 0 0 ${borders.radius.container}px ${borders.radius.container}px;
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 24px;
  gap: 16px;
`

export const Info = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const Name = styled(BodyText)`
  font-weight: ${fonts.weight.heavy};
  display: flex;
  gap: 4px;

  i {
    font-style: normal;
    color: ${colors.translucentSecondary50};
  }
`

export const Email = styled(LabelText)`
  color: ${colors.translucentSecondary70};
`

export const DetailsModalRow = styled.div`
  position: relative;
  width: -webkit-fill-available;
  padding: 24px 20px;
  height: 83px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: ${colors.translucentWhite60};
  border-radius: ${borders.radius.container}px;
`

export const TabContentWrapper = styled.div`
  padding: ${DETAILS_MODAL_PRIMARY_PADDING}px;
  height: 100%;
`

export const FlexTabContentWrapper = styled(TabContentWrapper)`
  display: flex;
  flex-direction: column;
`

export const SubHeaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

export const BorderedSubHeaderContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: space-between;
  border-radius: ${borders.radius.container}px;
  background: ${colors.translucentWhite60};
  padding: 16px;
  align-items: center;
  gap: 32px;
`

export const SubHeaderIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  background-color: ${colors.secondary10};
  border-radius: ${borders.radius.highlight}px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const SubHeaderSvgIconWrapper = styled(SubHeaderIconWrapper)`
  svg {
    width: 24px;
    height: 24px;
    ${svgPathStyles(colors.secondary, 1.5)}
  }
`

export const SubHeaderTextContent = styled.div`
  display: flex;
  flex-direction: column;
`

export const SubHeaderGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`

export const SubHeaderRowTextContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`

export const SubHeader = styled.div`
  color: ${colors.secondary};
  font-size: 18px;
  font-weight: ${fonts.weight.heavy};
  margin-bottom: 8px;
`

export const Description = styled.div`
  color: ${colors.secondary80};
  font-size: 14px;
  font-weight: ${fonts.weight.medium};
`

export const Plus = styled(SvgPlus)<{ $hasPayoutInstrument: boolean }>`
  height: 16px;
  width: 16px;
  top: 10px;
  bottom: 10px;
  cursor: auto;
  ${({ $hasPayoutInstrument }) =>
    svgPathStyles($hasPayoutInstrument ? colors.secondary : colors.white, 1.5)};
`

export const LoadingDetailsModalContentSection: React.FC = () => (
  <DetailsModalRow>
    <LoadingCircle width={35} height={35} />
    <Info>
      <LoadingBlock height={16} width={200} />
      <LoadingBlock height={15} width={100} />
    </Info>
  </DetailsModalRow>
)
